// One vocal clip on the timeline: tap = select + place playhead,
// drag (selected) = move, Range mode drag = select a section.
import React, { memo, useRef, useState } from "react";
import { WaveformCanvas } from "./WaveformCanvas";
import TrimHandle from "./TrimHandle";

const msAt = (e, el, off, pps) => off + ((e.clientX - el.getBoundingClientRect().left) / pps) * 1000;

function RangeOverlay({ range, off, win, pps, onRangeChange }) {
  const drag = useRef(null);
  const left = ((range.startMs - off) / 1000) * pps;
  const width = Math.max(2, ((range.endMs - range.startMs) / 1000) * pps);
  const handle = (edge) => ({
    onPointerDown: (e) => {
      e.stopPropagation(); e.preventDefault();
      e.currentTarget.setPointerCapture?.(e.pointerId);
      drag.current = { edge, x: e.clientX, s: range.startMs, en: range.endMs };
    },
    onPointerMove: (e) => {
      const d = drag.current;
      if (!d) return;
      e.stopPropagation();
      const dms = ((e.clientX - d.x) / pps) * 1000;
      const lo = off; const hi = off + win;
      if (d.edge === "start") onRangeChange({ startMs: Math.max(lo, Math.min(d.en - 50, d.s + dms)), endMs: d.en });
      else onRangeChange({ startMs: d.s, endMs: Math.min(hi, Math.max(d.s + 50, d.en + dms)) });
    },
    onPointerUp: (e) => { e.stopPropagation(); drag.current = null; },
    onPointerCancel: () => { drag.current = null; },
  });
  return (
    <div className="absolute inset-y-0 z-20 bg-white/20 border-x-2 border-white pointer-events-none"
         style={{ left, width }} data-testid="clip-range-overlay">
      {["start", "end"].map((edge) => (
        <div key={edge} {...handle(edge)} data-testid={`range-handle-${edge}`} role="slider" aria-label={`Range ${edge}`}
          className={`absolute top-0 bottom-0 w-6 pointer-events-auto flex items-center justify-center ${edge === "start" ? "-left-3" : "-right-3"}`}
          style={{ touchAction: "none" }}>
          <div className="w-3 h-7 rounded-sm bg-white shadow-[0_0_8px_rgba(255,255,255,0.7)]" />
        </div>
      ))}
    </div>
  );
}

export const VocalClip = memo(function VocalClip({
  take, colorHex, pps, height, selected, rangeMode, range, peaksInfo, preview, snap, disabled,
  onTap, onMoveCommit, onTrimPreview, onTrimCommit, onRangeChange,
}) {
  const k = preview ? { ...take, ...preview } : take;
  const [moveOff, setMoveOff] = useState(null);
  const drag = useRef(null);
  const cs = k.clip_start_ms || 0;
  const ce = k.clip_end_ms || k.duration_ms || 0;
  const win = Math.max(0, ce - cs);
  const off = moveOff ?? (k.beat_offset_ms || 0);
  const peaks = peaksInfo?.peaks || k.peaks || [];
  const rate = peaksInfo?.rate || (k.duration_ms ? (peaks.length / (k.duration_ms / 1000)) : 20);

  const onPointerDown = (e) => {
    if (disabled) return;
    e.stopPropagation();
    const mode = selected ? (rangeMode ? "range" : "move") : "tap";
    drag.current = { x: e.clientX, mode, moved: false, anchor: msAt(e, e.currentTarget, off, pps), orig: off };
    if (selected) e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e) => {
    const d = drag.current;
    if (!d) return;
    if (Math.abs(e.clientX - d.x) > 6) d.moved = true;
    if (!d.moved) return;
    if (d.mode === "move") {
      let next = Math.max(0, d.orig + ((e.clientX - d.x) / pps) * 1000);
      if (snap) next = Math.round(next / snap) * snap;
      d.next = Math.round(next);
      setMoveOff(d.next);
    } else if (d.mode === "range") {
      const cur = Math.max(off, Math.min(off + win, d.orig + ((e.clientX - e.currentTarget.getBoundingClientRect().left) / pps) * 1000));
      const a = Math.max(off, Math.min(off + win, d.anchor));
      onRangeChange?.({ takeId: take.take_id, trackId: take.track_id, startMs: Math.min(a, cur), endMs: Math.max(a, cur) });
    }
  };
  const onPointerUp = (e) => {
    const d = drag.current;
    drag.current = null;
    if (!d) return;
    e.stopPropagation();
    if (!d.moved) { onTap?.(take, d.anchor); return; }
    if (d.mode === "move" && d.next != null && d.next !== (take.beat_offset_ms || 0)) {
      onMoveCommit?.(take, d.next).finally(() => setMoveOff(null));
    } else setMoveOff(null);
  };
  const onPointerCancel = () => { drag.current = null; setMoveOff(null); };

  return (
    <div
      onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerCancel={onPointerCancel}
      onClick={(e) => e.stopPropagation()}
      className={`absolute top-0 bottom-0 rounded-md overflow-visible select-none ${selected ? "ring-2 ring-white z-10 bg-white/[0.07]" : "bg-white/[0.03] hover:ring-1 hover:ring-white/30"} ${selected && !rangeMode ? "cursor-grab" : "cursor-pointer"}`}
      style={{ left: (off / 1000) * pps, width: Math.max(6, (win / 1000) * pps), touchAction: selected ? "none" : "pan-x pan-y" }}
      data-testid={`take-clip-${take.take_id}`} data-offset-ms={off} data-window-ms={win}
      role="button" aria-label="Vocal clip" aria-pressed={selected}
    >
      <div className="absolute inset-0 overflow-hidden rounded-md">
        <WaveformCanvas peaks={peaks} rate={rate} srcStartMs={cs} srcEndMs={ce} pxPerSec={pps} height={height}
          color={`#${colorHex}`} gainDb={k.clip_gain_db || 0} fadeInMs={k.fade_in_ms || 0} fadeOutMs={k.fade_out_ms || 0}
          showFades={selected} testid={`take-wave-${take.take_id}`} />
      </div>
      {moveOff != null && (
        <span className="absolute -top-5 left-0 text-[10px] font-bold text-white bg-black/80 px-1 rounded" data-testid="clip-move-readout">
          {(moveOff / 1000).toFixed(2)}s
        </span>
      )}
      {selected && rangeMode && range && <RangeOverlay range={range} off={off} win={win} pps={pps} onRangeChange={(r) => onRangeChange?.({ ...range, ...r })} />}
      {selected && !rangeMode && (
        <>
          <TrimHandle edge="start" pxPerSecond={pps} clipStartMs={cs} clipEndMs={ce} durationMs={take.duration_ms || 0}
            onLivePreview={(ms) => onTrimPreview?.(take, { clip_start_ms: ms, clip_end_ms: ce, beat_offset_ms: (take.beat_offset_ms || 0) + (ms - (take.clip_start_ms || 0)) })}
            onCommit={(s, en) => onTrimCommit?.(take, s, en)} />
          <TrimHandle edge="end" pxPerSecond={pps} clipStartMs={cs} clipEndMs={ce} durationMs={take.duration_ms || 0}
            onLivePreview={(ms) => onTrimPreview?.(take, { clip_start_ms: cs, clip_end_ms: ms })}
            onCommit={(s, en) => onTrimCommit?.(take, s, en)} />
        </>
      )}
    </div>
  );
});
